import { useSelector } from "react-redux";
import { useOrganizationPlan } from "@/shared/hooks/useOrganizationFeatures";
import { useOrganizationQuery } from "@/shared/hooks/useOrganizationQuery";
import type { RootState } from "@/store";
import { DEV_MOCK_VALUE } from "../../configs/unpaidInvoices.config";
import { parseUnpaidInvoices } from "../../schemas/unpaidInvoices.schema";
import { resolveAccessState, resolveDevMockStatus } from "../../utils/unpaidAccess.utils";
import type { UnpaidAccessState, UnpaidInvoicesStatus, UnpaidPendingPayment } from "../../types/unpaidInvoices.types";

type UnpaidInvoicesAccess = {
  accessState: UnpaidAccessState
  amountCents: number
  pendingPayment: UnpaidPendingPayment | null
  paymentMethodRequired: boolean
  staffMessage: string | null
  isAdmin: boolean
  organizationId: string | undefined
  refreshOrganization: () => void
};

type UnpaidInvoicesAccessOptions = {
  /** Reads the organization on a timer: the console entry polls until the webhook lifts the block. */
  refetchInterval?: number | false
};

// The first read only: the first failure fails open (a slow or broken endpoint must not hide the
// console), and cached data is never worth a loader.
const isFirstReadPending = ({ isPending, failureCount }: { isPending: boolean, failureCount: number }) =>
  isPending && failureCount === 0;

// A null mock is a real answer ("normal"), only undefined means no mock applies.
const resolveStatus = (devMock: UnpaidInvoicesStatus | null | undefined, organization: unknown) =>
  devMock !== undefined ? devMock : parseUnpaidInvoices(organization);

const summarizeStatus = (status: UnpaidInvoicesStatus | null) => ({
  amountCents: status?.amountCents ?? 0,
  pendingPayment: status?.pendingPayment ?? null,
  paymentMethodRequired: status?.paymentMethodRequired ?? false,
  staffMessage: status?.staffMessage ?? null,
});

export const useUnpaidInvoicesAccess = ({ refetchInterval = false }: UnpaidInvoicesAccessOptions = {}): UnpaidInvoicesAccess => {
  const isAuthenticated = useSelector((state: RootState) => state.session.isAuthenticated);
  const organizationId = useSelector((state: RootState) => state.session.user?.representative.organization_id);
  const isImpersonating = useSelector(
    (state: RootState) => state.session.user?.representative.impersonation?.active === true,
  );
  // The router fetches a missing plan, but it is not mounted while the gate blocks the console.
  const { plan } = useOrganizationPlan();
  // Focus is the cheap moment to notice a settled payment; an organization with nothing owed has no reason to refetch.
  const organizationQuery = useOrganizationQuery(isAuthenticated ? organizationId : undefined, {
    refetchOnWindowFocus: (query) => parseUnpaidInvoices(query.state.data?.organization) !== null,
    refetchInterval,
  });

  // Guarding the call site lets the bundler drop the mock resolver from production builds; the
  // mock only applies to a signed-in session, otherwise signing out would land back on the blocked view.
  const devMock = import.meta.env.DEV && isAuthenticated
    ? resolveDevMockStatus(DEV_MOCK_VALUE)
    : undefined;
  const status = resolveStatus(devMock, organizationQuery.data?.organization);

  const isLoading = isAuthenticated
    && !isImpersonating
    && Boolean(organizationId)
    && devMock === undefined
    && isFirstReadPending(organizationQuery);

  return {
    accessState: isLoading ? "loading" : resolveAccessState(status, isImpersonating),
    ...summarizeStatus(status),
    isAdmin: plan?.direct_debit_manage === true,
    organizationId,
    refreshOrganization: () => void organizationQuery.refetch(),
  };
};
